/**
 * Beta Auth Callback Page
 * Handles Emergent-managed Google Auth callback for beta registration
 * Processes session_id from URL fragment and creates beta user account
 * 
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
 */
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, AlertCircle } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const BetaAuthCallback: React.FC = () => {
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const hasProcessed = useRef(false);
  
  useEffect(() => {
    // Prevent double processing in StrictMode
    if (hasProcessed.current) return;
    hasProcessed.current = true;
    
    const processAuth = async () => {
      try {
        // Get session_id from URL fragment (after #)
        const hash = window.location.hash;
        const sessionIdMatch = hash.match(/session_id=([^&]+)/);
        const sessionId = sessionIdMatch ? sessionIdMatch[1] : null;
        
        // Get beta_token from localStorage (stored before Google redirect)
        const betaToken = localStorage.getItem('pending_beta_token');
        
        if (!sessionId) {
          setError('Ingen autentiseringsdata funnet. Prøv igjen.');
          return;
        }
        
        if (!betaToken) {
          setError('Ingen beta-token funnet. Vennligst start registreringen på nytt.');
          return;
        }
        
        // Clear the pending token
        localStorage.removeItem('pending_beta_token');
        
        // Send session_id to our backend - it will exchange with Emergent API
        // This avoids CORS issues since backend-to-backend calls are allowed
        const registerResponse = await axios.post(`${API}/api/beta/register-google-session`, {
          beta_token: betaToken,
          session_id: sessionId
        });
        
        if (registerResponse.data.success) {
          // Store token and redirect to dashboard
          localStorage.setItem('token', registerResponse.data.token);
          localStorage.setItem('user', JSON.stringify(registerResponse.data.user));
          
          // Redirect to dashboard
          navigate('/lexy/dashboard', { replace: true });
        } else {
          throw new Error(registerResponse.data.message || 'Registrering feilet');
        }
        
      } catch (err: any) {
        console.error('Beta auth callback error:', err);
        // Clear pending token on error
        localStorage.removeItem('pending_beta_token');
        setError(err.response?.data?.detail || err.message || 'Autentisering feilet. Prøv igjen.');
      }
    };
    
    processAuth();
  }, [navigate]);
  
  if (error) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4">
        <div className="bg-slate-900 border border-slate-800 rounded-2xl p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <AlertCircle className="w-8 h-8 text-red-400" />
          </div>
          <h1 className="text-xl font-bold text-white mb-2">Autentisering feilet</h1>
          <p className="text-slate-400 mb-6">{error}</p>
          <button
            onClick={() => navigate('/')}
            className="px-4 py-2 bg-slate-800 text-white rounded-lg hover:bg-slate-700 transition-colors"
          >
            Gå til forsiden
          </button>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center">
      <div className="text-center">
        <Loader2 className="w-8 h-8 animate-spin text-violet-500 mx-auto mb-4" />
        <p className="text-slate-400">Oppretter din konto...</p>
      </div>
    </div>
  );
};

export default BetaAuthCallback;
